import { ReactNode } from "react";
import { Check, ChevronDown, ChevronRight, Hash, PenLine, Quote, Sparkles, TriangleAlert } from "lucide-react";
import { FieldProvenance, ProposedField } from "./types";

interface MetadataReviewRowProps {
  label: string;
  /** What the reviewer should be looking for. Shown only while the row is open. */
  hint: string;
  /** What the reader proposed. Absent for a field it did not attempt. */
  proposed?: ProposedField;
  /** The reader's proposals for a multi-level field, so each level shows its own provenance. */
  proposedLevels?: ProposedField[];
  /** The value as it currently stands — the reader's, or whatever has been typed over it. */
  display: string;
  verified: boolean;
  open: boolean;
  onToggle: () => void;
  onConfirm: () => void;
  /** The editor for this field. Editing is what confirming looks like when the reader was wrong. */
  children: ReactNode;
}

/** What the mark means, spelled out on hover — the only place the words are still worth their space. */
const PROVENANCE_LABEL: Record<FieldProvenance, string> = {
  found: "Found in the document, and quoted",
  inferred: "Inferred from other evidence in the document",
  suggested: "Suggested — this appears nowhere in the document",
  generated: "Library code — issued by IQ Verity, not read from the document",
};

/** The longest run of whole words every string begins with. */
function commonPrefix(values: string[]): string {
  const [first, ...rest] = values;
  if (!first) return "";
  let end = first.length;
  for (const other of rest) {
    let i = 0;
    while (i < end && i < other.length && first[i] === other[i]) i++;
    end = i;
  }
  // Back off to a word boundary so a prefix never ends mid-word.
  const cut = first.slice(0, end);
  return cut.slice(0, Math.max(cut.lastIndexOf(" "), 0));
}

/**
 * One rationale instead of several near-identical ones.
 *
 * A multi-level field asks the reader about each level separately, so it answers about each level
 * separately — and when two levels rest on the SAME evidence you get the same sentence twice with a
 * different ending. Nobody reads the second one. Duplicates go, notes wholly contained in another go,
 * and what shares an opening is folded back into a single sentence.
 */
export function mergeNotes(notes: string[]): string[] {
  const cleaned = [...new Set(notes.map((n) => n.trim()).filter(Boolean))];
  const kept = cleaned.filter((n) => !cleaned.some((other) => other !== n && other.includes(n)));
  if (kept.length < 2) return kept;

  const shared = commonPrefix(kept);
  // Fold at the last clause break inside the shared opening, so the joined tails read as clauses
  // rather than being spliced mid-sentence.
  const breakAt = Math.max(shared.lastIndexOf(","), shared.lastIndexOf(";"));
  if (breakAt < 20) return kept;

  const stem = shared.slice(0, breakAt);
  const tails = kept
    .map((n) => n.slice(breakAt + 1).trim().replace(/\.$/, ""))
    .filter(Boolean);

  return tails.length === kept.length ? [`${stem}, ${tails.join(", and ")}.`] : kept;
}

/**
 * One field of the standard's identity, confirmed on its own.
 *
 * Modelled on the TOSP lease review rather than on a pre-filled form, and the difference is the point:
 * a form that arrives populated invites a reviewer to scroll past it, whereas a row that stays
 * outstanding until it is opened makes each value somebody's explicit decision. Where the reader found
 * nothing the row still has to be dealt with — you type the value and confirm it, which is the same
 * act with a different starting point.
 */
const MetadataReviewRow = ({
  label,
  hint,
  proposed,
  proposedLevels,
  display,
  verified,
  open,
  onToggle,
  onConfirm,
  children,
}: MetadataReviewRowProps) => {
  const levels = proposedLevels ?? (proposed ? [proposed] : []);
  // The weakest link governs: a path that is one found municipality and two inferred levels is not a
  // found path, and showing it as one would overstate what the document says.
  // A generated value is not on the found/inferred/suggested ladder at all — it is the library's own,
  // so a field made of one is simply that and nothing weighs against it.
  const generated = levels.length > 0 && levels.every((l) => l.provenance === "generated");
  const weakest: FieldProvenance = generated
    ? "generated"
    : levels.some((l) => l.provenance === "suggested")
      ? "suggested"
      : levels.some((l) => l.provenance === "inferred")
        ? "inferred"
        : "found";

  const confidence =
    levels.length && !generated
      ? Math.round(Math.min(...levels.map((l) => l.confidence)) * 100)
      : null;

  const notes = generated ? [] : mergeNotes(levels.map((l) => l.note).filter(Boolean) as string[]);
  // Two levels can quote the same sentence — the cover line naming the town also names the authority.
  const quotes = levels.filter(
    (l, i) => l.quote && levels.findIndex((other) => other.quote === l.quote) === i,
  );
  const empty = display.trim().length === 0;
  const provenanceTitle =
    levels.length === 0 ? "Entered by hand — the reader proposed nothing here" : PROVENANCE_LABEL[weakest];

  return (
    <div className={`vr-review-row ${verified ? "vr-verified" : ""} ${open ? "vr-open" : ""}`}>
      <button type="button" className="vr-review-head" onClick={onToggle}>
        <span className="vr-review-chev">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>
        <span className="vr-review-label">{label}</span>
        <span className={`vr-review-value ${empty ? "vr-review-empty" : ""}`}>
          {empty ? "Not found — enter it" : display}
        </span>
        {/* A mark, not a sentence. Six rows each captioned "Found in document" is a wall of text that
            says nothing once you have read the first one — and after verification the row is a record,
            not a question. The word is still there on hover, for the one time it is wanted. */}
        <span
          className={`vr-prov vr-prov-${levels.length === 0 ? "manual" : weakest}`}
          title={provenanceTitle}
          aria-label={provenanceTitle}
        >
          {levels.length === 0 ? (
            <PenLine size={11} />
          ) : (
            <>
              {weakest === "found" && <Quote size={11} />}
              {weakest === "inferred" && <TriangleAlert size={11} />}
              {weakest === "suggested" && <Sparkles size={11} />}
              {weakest === "generated" && <Hash size={11} />}
            </>
          )}
        </span>
        <span className={`vr-review-state ${verified ? "vr-done" : ""}`}>
          {verified ? (
            <>
              <Check size={11} /> Verified
            </>
          ) : (
            "To review"
          )}
        </span>
      </button>

      {open && (
        <div className="vr-review-body">
          {/* Guidance only for a field with no evidence behind it — one the reader missed, or one the
              library issued itself. Where there IS evidence, it is more use than advice about where
              to go looking for it. */}
          {(levels.length === 0 || generated) && <p className="vr-review-hint">{hint}</p>}

          {quotes.length > 0 && (
            <div className="vr-quotes">
              {quotes.map((level, i) => (
                <blockquote key={i} className="vr-quote">
                  <span className="vr-quote-text">“{level.quote}”</span>
                  {level.pdfPage !== undefined && (
                    <span className="vr-quote-src">page {level.pdfPage}</span>
                  )}
                </blockquote>
              ))}
            </div>
          )}

          {/* One line: why the reader believes this, and how strongly. The chip above already says
              whether it was found, inferred or suggested — repeating it in a sentence is noise. */}
          {(notes.length > 0 || confidence !== null) && (
            <p className={`vr-review-note ${weakest === "found" ? "vr-note-plain" : ""}`}>
              {weakest !== "found" && <TriangleAlert size={11} />}
              <span>
                {notes.join(" ")}
                {confidence !== null && (
                  <span className="vr-note-conf">
                    {notes.length > 0 ? " · " : ""}
                    {confidence}% confident
                  </span>
                )}
              </span>
            </p>
          )}

          <div className="vr-review-editor">{children}</div>

          <div className="vr-review-actions">
            <button type="button" className="vr-btn vr-btn-outline vr-btn-sm" onClick={onConfirm}>
              <Check size={13} /> {verified ? "Confirmed" : "Confirm"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetadataReviewRow;
